'use client';

import { useState } from 'react';
import Image from 'next/image';
import { VideoSection } from '@/types/sanity';
import { SectionCTAs } from './SectionCTAs';
import { PortableText } from '@portabletext/react';
import { ScrollReveal } from '@/components/animations/ScrollAnimations';
import { urlFor } from '@/sanity/lib/image';
import { Play } from 'lucide-react';

import { cn } from '@/lib/utils';
import { getEmbedUrl } from '@/lib/utils/video';

interface VideoSectionComponentProps {
  section: VideoSection;
  pageSlug?: string;
}

export function VideoSectionComponent({ section, pageSlug }: VideoSectionComponentProps) {
  const layout = section.layout || 'contained';
  const embedUrl = getEmbedUrl(section.videoUrl);
  const hasPoster = !!section.posterImage?.asset;
  const [playing, setPlaying] = useState(!hasPoster);

  const videoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: section.title || 'Video',
    ...(section.subtitle && { description: section.subtitle }),
    embedUrl: embedUrl || section.videoUrl,
    ...(hasPoster && {
      thumbnailUrl: urlFor(section.posterImage!).width(1280).height(720).url(),
    }),
  };

  const videoEmbed = embedUrl ? (
    <div className="relative w-full aspect-video rounded-lg overflow-hidden shadow-lg">
      {!playing && hasPoster ? (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full group cursor-pointer"
          aria-label="Play video"
        >
          <Image
            src={urlFor(section.posterImage!).width(1280).height(720).quality(85).url()}
            alt={section.posterImage?.alt || section.title || 'Video thumbnail'}
            fill
            className="object-cover"
          />
          <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="h-16 w-16 md:h-20 md:w-20 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-lg transition-colors">
              <Play className="h-7 w-7 md:h-9 md:w-9 text-primary ml-1" />
            </div>
          </div>
        </button>
      ) : (
        <iframe
          src={`${embedUrl}${hasPoster ? '?autoplay=1' : ''}`}
          title={section.title || 'Video'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sandbox="allow-same-origin allow-scripts allow-presentation allow-popups"
          className="absolute inset-0 w-full h-full"
        />
      )}
    </div>
  ) : null;

  return (
    <ScrollReveal direction="up" delay={0.1}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(videoJsonLd) }}
      />
      <section
        className={cn(
          'video-section py-8 md:py-12 lg:py-16',
          pageSlug && `video-section--${pageSlug}`
        )}
      >
        <div
          className={cn(
            'mx-auto sm:px-6 lg:px-8',
            layout === 'full-width' ? 'max-w-full' : 'max-w-[90rem]'
          )}
        >
          <div className="px-6 sm:px-0">
            {(section.title || section.subtitle) && (
              <div className="mb-8 md:mb-10 text-center">
                {section.title && (
                  <h2 className="font-medium mb-3 text-section-title leading-section-title">
                    {section.title}
                  </h2>
                )}
                {section.subtitle && (
                  <p className="text-muted-foreground text-section-subtitle leading-section-subtitle">
                    {section.subtitle}
                  </p>
                )}
              </div>
            )}

            {layout === 'side-by-side' ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>{videoEmbed}</div>
                {section.description && (
                  <div className="prose prose-lg max-w-none">
                    <PortableText value={section.description} />
                  </div>
                )}
              </div>
            ) : (
              <div className={layout === 'contained' ? 'max-w-4xl mx-auto' : ''}>{videoEmbed}</div>
            )}

            <SectionCTAs ctas={section.ctas} />
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
